// Rigid weapons held in a premium unit's hand (2026-10-25). A long weapon drawn into the mesh gets skin weights from
// the arm and the legs it reaches and bends like a whip (the round 3 yari), so long weapons are drawn out of the sheet
// (an empty fist) and built here, then fixed to the hand bone: the hand's motion carries them, nothing bends.
// Each clip gets its own grip: the prop is aimed at that clip's peak (the frame where the hand reaches furthest
// forward) and aimProps() swaps the grip in when the clip starts. One grip for every clip could not work, since the
// clips were made for an empty hand: aimed in the rest pose the yari pointed at the sky mid-thrust, aimed at the
// thrust it dragged on the ground in the walk (renders, 2026-10-25).
// Moves (2026-10-25, round 5): Meshy's library has no spear attack, and its walk and run swing the arms, which drove
// the yari into the ground. So a spear unit is rigged with a two-handed charge as its run (654 Rifle_Charge), the walk
// keeps the charge's arms (`carry`), and the attack is made here from the charge's grip (`thrust`): a wind-up, a lunge
// with the hips and chest, and the yari driving forward through the fist.
import * as THREE from 'three';
import type { ArmyId } from './premium';

export interface PropSpec {
  bone: string;
  /** Node name, so a clip can move the prop (the thrust's drive). */
  name: string;
  /** Builds the prop along +y, its grip at the origin, for a figure this tall. */
  make: (h: number) => THREE.Object3D;
  /** Per clip: where the prop points at that clip's peak, in the unit's own space (+z is forward, y up). */
  aims: Record<string, [number, number, number]>;
  /** The clip whose peak pose is the weapon grip, for carry and thrust. */
  hold?: string;
  /** Clips that keep the hold's arms (their legs and body still move). */
  carry?: string[];
  /** Make an `attack` clip from the hold's grip. */
  thrust?: boolean;
}

const wood = new THREE.MeshStandardMaterial({ color: '#6b4a2b', roughness: 0.6 });
const steel = new THREE.MeshStandardMaterial({ color: '#d4dade', roughness: 0.3, metalness: 0.5 });

/** A yari: a straight shaft 1.5 figures long, held a third of the way up, with a narrow straight blade. */
function yari(h: number) {
  const g = new THREE.Group(), L = 1.5 * h, r = 0.013 * h, grip = 0.33 * L;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r, L, 8), wood);
  shaft.position.y = L / 2 - grip;
  // flattened in its geometry, not by scale: the match bake turns normals by the part matrix, right only for uniform scale
  const blade = new THREE.Mesh(new THREE.ConeGeometry(r * 2.2, 0.16 * h, 4).scale(1, 1, 0.35), steel);
  blade.position.y = L - grip + 0.08 * h;
  g.add(shaft, blade);
  return g;
}

/** A halberd (Knights, 2026-10-26): a shaft a little taller than the man, an axe blade and a back hook under a spike. */
function halberd(h: number) {
  const g = new THREE.Group(), L = 1.35 * h, r = 0.014 * h, grip = 0.33 * L, top = L - grip;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r, L, 8), wood);
  shaft.position.y = L / 2 - grip;
  const spike = new THREE.Mesh(new THREE.ConeGeometry(r * 1.8, 0.14 * h, 4).scale(1, 1, 0.4), steel);
  spike.position.y = top + 0.07 * h;
  // the blade forward of the shaft (+z of the prop's own frame, which the grips keep facing the enemy), the hook behind
  const blade = new THREE.Mesh(new THREE.BoxGeometry(0.012 * h, 0.13 * h, 0.1 * h), steel);
  blade.position.set(0, top - 0.06 * h, 0.06 * h);
  const hook = new THREE.Mesh(new THREE.ConeGeometry(r * 1.6, 0.08 * h, 4).rotateX(-Math.PI / 2).scale(0.4, 1, 1), steel);
  hook.position.set(0, top - 0.05 * h, -0.05 * h);
  g.add(shaft, spike, blade, hook);
  return g;
}

/** A sarissa (Greeks): a pike twice the man's height, held near its butt (a seventh of the way up, so carried upright the butt
 *  clears the ground), a small leaf head and a butt spike. */
function sarissa(h: number) {
  const g = new THREE.Group(), L = 2.2 * h, r = 0.012 * h, grip = 0.15 * L;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r, L, 8), wood);
  shaft.position.y = L / 2 - grip;
  const head = new THREE.Mesh(new THREE.ConeGeometry(r * 2.4, 0.15 * h, 4).scale(1, 1, 0.35), steel);
  head.position.y = L - grip + 0.075 * h;
  const butt = new THREE.Mesh(new THREE.ConeGeometry(r * 1.4, 0.07 * h, 4).rotateX(Math.PI), steel);
  butt.position.y = -grip - 0.035 * h;
  g.add(shaft, head, butt);
  return g;
}

/** A Viking spear: a shaft 1.4 figures long and a broad leaf blade. */
function vikingSpear(h: number) {
  const g = new THREE.Group(), L = 1.4 * h, r = 0.014 * h, grip = 0.33 * L;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r, L, 8), wood);
  shaft.position.y = L / 2 - grip;
  const blade = new THREE.Mesh(new THREE.OctahedronGeometry(1, 0).scale(r * 3.2, 0.11 * h, r * 0.9), steel);
  blade.position.y = L - grip + 0.09 * h;
  g.add(shaft, blade);
  return g;
}

/** A javelin (the Greek peltast): shorter than the man, held at its middle, an iron point. */
function javelin(h: number) {
  const g = new THREE.Group(), L = 0.95 * h, r = 0.011 * h, grip = 0.45 * L;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r, L, 6), wood);
  shaft.position.y = L / 2 - grip;
  const tip = new THREE.Mesh(new THREE.ConeGeometry(r * 1.8, 0.08 * h, 4), steel);
  tip.position.y = L - grip + 0.04 * h;
  g.add(shaft, tip);
  return g;
}

const obsidian = new THREE.MeshStandardMaterial({ color: '#1b1a1f', roughness: 0.25, metalness: 0.2 });
const bronze = new THREE.MeshStandardMaterial({ color: '#8a6a45', roughness: 0.4, metalness: 0.45 });
const bamboo = new THREE.MeshStandardMaterial({ color: '#8f8a4f', roughness: 0.6 });

/** A shaft L figures long held `grip` of the way up, and a head built at its top. */
function shafted(h: number, L: number, grip: number, r: number, mat: THREE.Material, head: (g: THREE.Group, top: number) => void) {
  const g = new THREE.Group(), len = L * h, gr = grip * len;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r * h, r * h, len, 8), mat);
  shaft.position.y = len / 2 - gr;
  g.add(shaft);
  head(g, len - gr);
  return g;
}

/** The Mongol hook lance (2026-10-26, armies2): a long shaft, a narrow blade and a hook curling back under it, to pull
 *  a rider down. */
const hookLance = (h: number) => shafted(h, 1.55, 0.33, 0.013, wood, (g, top) => {
  const blade = new THREE.Mesh(new THREE.ConeGeometry(0.028 * h, 0.15 * h, 4).scale(1, 1, 0.35), steel); blade.position.y = top + 0.075 * h;
  const hook = new THREE.Mesh(new THREE.TorusGeometry(0.035 * h, 0.008 * h, 4, 8, Math.PI), steel); hook.rotation.y = Math.PI / 2; hook.position.set(0, top - 0.02 * h, -0.035 * h);
  g.add(blade, hook);
});

/** The Chinese wolf-brush spear (the langxian): a bamboo pole whose top third keeps its branches, a bushy outline at
 *  any size, and an iron point. */
const langxian = (h: number) => shafted(h, 1.5, 0.3, 0.014, bamboo, (g, top) => {
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.024 * h, 0.12 * h, 4), steel); tip.position.y = top + 0.06 * h; g.add(tip);
  for (let k = 0; k < 9; k++) {
    const y = top - 0.06 * h - k * 0.045 * h, len = (0.08 + 0.012 * k) * h, a = k * 2.4;
    const twig = new THREE.Mesh(new THREE.CylinderGeometry(0.004 * h, 0.006 * h, len, 4), bamboo);
    twig.position.set(Math.cos(a) * len * 0.4, y, Math.sin(a) * len * 0.4);
    twig.rotation.set(Math.sin(a) * 0.9, 0, -Math.cos(a) * 0.9);
    g.add(twig);
  }
});

/** The Chinese repeating crossbow: a stock along the aim, a short bow across its front and the box magazine on top. */
function crossbow(h: number) {
  const g = new THREE.Group();
  const stock = new THREE.Mesh(new THREE.BoxGeometry(0.035 * h, 0.42 * h, 0.04 * h), wood); stock.position.y = 0.12 * h;
  const bow = new THREE.Mesh(new THREE.BoxGeometry(0.34 * h, 0.025 * h, 0.03 * h), wood); bow.position.y = 0.3 * h;
  const mag = new THREE.Mesh(new THREE.BoxGeometry(0.045 * h, 0.2 * h, 0.07 * h), wood); mag.position.set(0, 0.16 * h, 0.05 * h);
  const lever = new THREE.Mesh(new THREE.BoxGeometry(0.015 * h, 0.22 * h, 0.015 * h), steel); lever.position.set(0, 0.06 * h, 0.08 * h); lever.rotation.x = 0.5;
  g.add(stock, bow, mag, lever);
  return g;
}

/** The Aztec broadspear (the tepoztopilli): a wide flat wooden head edged with black stone blades. */
const broadspear = (h: number) => shafted(h, 1.35, 0.33, 0.014, wood, (g, top) => {
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.07 * h, 0.2 * h, 0.018 * h), wood); head.position.y = top + 0.1 * h; g.add(head);
  for (const x of [-1, 1]) for (let k = 0; k < 4; k++) {
    const b = new THREE.Mesh(new THREE.ConeGeometry(0.014 * h, 0.04 * h, 3).rotateZ((-x * Math.PI) / 2), obsidian);
    b.position.set(x * 0.045 * h, top + 0.04 * h + k * 0.045 * h, 0); g.add(b);
  }
  const point = new THREE.Mesh(new THREE.ConeGeometry(0.035 * h, 0.06 * h, 4).scale(1, 1, 0.3), obsidian); point.position.y = top + 0.23 * h; g.add(point);
});

/** The Aztec dart and its thrower (the atlatl): a long thin dart with a stone point, laid along a short board. */
const dart = (h: number) => shafted(h, 1.0, 0.3, 0.008, wood, (g, top) => {
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.016 * h, 0.06 * h, 4), obsidian); tip.position.y = top + 0.03 * h;
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.03 * h, 0.3 * h, 0.012 * h), wood); board.position.set(0, -0.05 * h, -0.015 * h);
  g.add(tip, board);
});

/** The Egyptian spear: a long shaft and a dark bronze leaf blade. */
const nileSpear = (h: number) => shafted(h, 1.45, 0.33, 0.013, wood, (g, top) => {
  const blade = new THREE.Mesh(new THREE.OctahedronGeometry(1, 0).scale(0.03 * h, 0.1 * h, 0.012 * h), bronze); blade.position.y = top + 0.08 * h; g.add(blade);
});

/** A cavalry lance (2026-10-27, horses round): a long shaft held near its back end, a cone guard over the fist and a
 *  narrow steel point. L is its length in figures (the Knights lancer's is the longest). */
const lance = (L: number) => (h: number) => shafted(h, L, 0.22, 0.014, wood, (g, top) => {
  const point = new THREE.Mesh(new THREE.ConeGeometry(0.024 * h, 0.14 * h, 4), steel); point.position.y = top + 0.07 * h;
  const guard = new THREE.Mesh(new THREE.ConeGeometry(0.05 * h, 0.12 * h, 8, 1, true), steel); guard.position.y = 0.1 * h;
  g.add(point, guard);
});

/** The naginata: a shaft and a long curved blade, the curve made of three straight pieces bending back. */
const naginata = (h: number) => shafted(h, 1.4, 0.3, 0.013, wood, (g, top) => {
  for (let k = 0; k < 3; k++) {
    const piece = new THREE.Mesh(new THREE.BoxGeometry(0.035 * h, 0.1 * h, 0.01 * h), steel);
    piece.position.set(0, top + 0.05 * h + k * 0.095 * h, -0.012 * h * k * k); piece.rotation.x = -0.12 * k; g.add(piece);
  }
});

/** A rider's weapon (2026-10-27, horses round): in his right hand, sloped up and forward while he rides and levelled
 *  at the foe at his strike's peak. Riders have no run to take a grip from, so nothing is carried or made. */
const rideWith = (name: string, make: (h: number) => THREE.Object3D, ride: [number, number, number] = [0, 1, 0.45]): PropSpec =>
  ({ bone: 'RightHand', name, make, aims: { ride, attack: [0, -0.08, 1] } });

/** A spear unit: a long weapon on the charge's grip, the march carried, the thrust made (see the file's head). */
const pole = (name: string, make: (h: number) => THREE.Object3D, walk: [number, number, number] = [0, 1.2, 1]): PropSpec =>
  ({ bone: 'RightHand', name, make, hold: 'run', carry: ['walk'], thrust: true, aims: { attack: [0, -0.05, 1], run: [0, 0.12, 1], walk } });

export const PROPS: Record<string, PropSpec[]> = {
  // the thrust lands level and a touch down; on the charge the yari is levelled ahead, on the march sloped up
  spear: [pole('yari', yari)],
  cavalry: [rideWith('naginata', naginata)],
};

/** Every army's props, by unit file (2026-10-26). An army's spearman is drawn with an empty fist and rigged with the
 *  charge, like the Samurai's, so one spec shape serves every pole; the sarissa marches near upright, since sloped
 *  forward a pike twice his height reaches a soldier and a half ahead. The peltast's javelin rides his hand in every
 *  clip with no made moves: aimed up on the march and forward at the throw's peak. */
export const ARMY_PROPS: Partial<Record<ArmyId, Record<string, PropSpec[]>>> = {
  samurai: PROPS,
  knights: { spear: [pole('halberd', halberd)], cavalry: [rideWith('lance', lance(1.5))], elite: [rideWith('lance', lance(1.8))] },
  greeks: {
    spear: [pole('sarissa', sarissa, [0, 3, 0.5])],
    ranged: [{ bone: 'RightHand', name: 'javelin', make: javelin, aims: { attack: [0, 0.2, 1], walk: [0, 1, 0.35], run: [0, 1, 0.5] } }],
    cavalry: [{ ...rideWith('javelin', javelin), aims: { ride: [0, 1, 0.35], attack: [0, 0.2, 1] } }],
  },
  vikings: { spear: [pole('spear', vikingSpear)] },
  // 2026-10-26, armies2. The crossbow rides the bow hand (the left, held out at the shot, the clip being a bow's) and
  // the dart the throwing hand, as the peltast's javelin.
  mongols: { spear: [pole('lance', hookLance)], elite: [rideWith('lance', lance(1.6))] },
  chinese: {
    spear: [pole('langxian', langxian)],
    ranged: [{ bone: 'LeftHand', name: 'crossbow', make: crossbow, aims: { attack: [0, 0.05, 1], walk: [0, 0.5, 1], run: [0, 0.5, 1] } }],
    cavalry: [rideWith('lance', lance(1.5))],
  },
  aztecs: {
    spear: [pole('broadspear', broadspear)],
    ranged: [{ bone: 'RightHand', name: 'dart', make: dart, aims: { attack: [0, 0.2, 1], walk: [0, 1, 0.35], run: [0, 1, 0.5] } }],
  },
  egyptians: { spear: [pole('spear', nileSpear)] },
  neutral: { spear: [pole('spear', vikingSpear)] },
};

/** A made thrust's strike time. Its peak is not the furthest reach: the hands sit below the chest's pivot, so the
 *  lean that drives the strike carries them back a little (render, 2026-10-25). */
const STRIKE = new WeakMap<THREE.AnimationClip, number>();
const ARMS = ['Shoulder', 'Arm', 'ForeArm', 'Hand'].flatMap((b) => ['Left' + b, 'Right' + b]);
const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _q = new THREE.Quaternion(), _up = new THREE.Vector3(0, 1, 0);

/** Fix the unit's props to its bones. Call once, right after load, before any mixer runs. Leaves the rest pose as it
 *  found it. Each prop keeps one grip per clip (userData.aims, plain arrays so a SkeletonUtils clone keeps them). A
 *  spec with carry or thrust edits `clips` in place (a carried clip's arm tracks, a new `attack`). */
export function applyProps(unit: string, root: THREE.Object3D, clips: THREE.AnimationClip[], specs = PROPS[unit]) {
  if (!specs) return [];
  root.updateMatrixWorld(true);
  // the figure's height in the unit's own units: the prop is built in them and keeps the root's scale on top
  const hw = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3()).y, h = hw / root.getWorldScale(new THREE.Vector3()).y;
  const rest = new Map<THREE.Object3D, [THREE.Vector3, THREE.Quaternion, THREE.Vector3]>();
  root.traverse((o) => rest.set(o, [o.position.clone(), o.quaternion.clone(), o.scale.clone()]));
  const restore = () => { for (const [o, [p, q, sc]] of rest) { o.position.copy(p); o.quaternion.copy(q); o.scale.copy(sc); } root.updateMatrixWorld(true); };
  const hips = root.getObjectByName('Hips') ?? root; // reach is measured from the hips, so a lunge's root motion doesn't count
  /** Pose the unit at the clip's peak (a made thrust's strike, else the hand furthest forward); the caller restores. */
  const atPeak = (clip: THREE.AnimationClip, bone: THREE.Object3D) => {
    const mixer = new THREE.AnimationMixer(root), act = mixer.clipAction(clip).play();
    let best = STRIKE.get(clip) ?? 0, reach = STRIKE.has(clip) ? Infinity : -Infinity;
    for (let k = 0; k <= 60 && reach < Infinity; k++) {
      mixer.setTime((clip.duration * k) / 60); root.updateMatrixWorld(true);
      const z = root.worldToLocal(bone.getWorldPosition(_a)).z - root.worldToLocal(hips.getWorldPosition(_b)).z;
      if (z > reach) { reach = z; best = (clip.duration * k) / 60; }
    }
    mixer.setTime(best); root.updateMatrixWorld(true);
    return () => { act.stop(); mixer.uncacheRoot(root); };
  };
  return specs.map((s) => {
    const bone = root.getObjectByName(s.bone);
    if (!bone || !bone.parent) throw new Error(`${unit}: no ${s.bone} bone for its prop`);
    const find = (n: string) => clips.find((c) => c.name === n);
    if (s.hold) {
      const hold = find(s.hold);
      if (!hold) throw new Error(`${unit}: no ${s.hold} clip to take its grip from`);
      if (hips === root || !hips.parent) throw new Error(`${unit}: no Hips bone to build its moves on`);
      const done = atPeak(hold, bone);
      for (const c of s.carry ?? []) { const clip = find(c); if (clip) carry(root, clip, hold); }
      if (s.thrust) {
        const old = clips.findIndex((c) => c.name === 'attack'); // the made thrust replaces any attack in the file
        if (old >= 0) clips.splice(old, 1);
        clips.push(thrust(root, hips, hold, h));
      }
      done(); restore();
    }
    const prop = s.make(h), aims: Record<string, number[]> = {};
    prop.name = s.name;
    bone.add(prop);
    // built in the unit's units: undo whatever scale the armature puts on its bones (Meshy's is in centimetres)
    const bs = bone.getWorldScale(new THREE.Vector3()), rs = root.getWorldScale(new THREE.Vector3());
    prop.scale.set(rs.x / bs.x, rs.y / bs.y, rs.z / bs.z);
    // the grip point is fixed in the hand: the fist's middle, a little past the wrist along the forearm
    bone.getWorldPosition(_a); bone.parent.getWorldPosition(_b);
    prop.position.copy(bone.worldToLocal(_a.clone().addScaledVector(_a.clone().sub(_b).normalize(), 0.035 * hw)));
    for (const [name, dir] of Object.entries(s.aims)) {
      const clip = find(name);
      if (!clip) continue;
      const done = atPeak(clip, bone);
      // the world turn that points +y along dir, then taken into the hand's frame
      root.getWorldQuaternion(_q);
      const world = new THREE.Quaternion().setFromUnitVectors(_up, new THREE.Vector3(...dir).normalize().applyQuaternion(_q));
      aims[name] = bone.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(world).toArray();
      done(); restore();
    }
    // the drive: the yari slides forward through the fist on the strike, along its attack grip
    const atk = s.thrust && find('attack');
    if (atk && aims.attack) {
      const p0 = prop.position.clone(), along = _up.clone().applyQuaternion(new THREE.Quaternion().fromArray(aims.attack));
      const p1 = p0.clone().addScaledVector(along, (0.18 * h * rs.y) / bs.y);
      atk.tracks.push(new THREE.VectorKeyframeTrack(`${s.name}.position`, [0, 0.3, 0.45, 0.8], [...p0.toArray(), ...p0.toArray(), ...p1.toArray(), ...p0.toArray()]));
    }
    prop.userData.aims = aims;
    prop.userData.prop = true;
    const first = Object.values(aims)[0];
    if (first) prop.quaternion.fromArray(first);
    return prop;
  });
}

type Prop = 'position' | 'quaternion' | 'scale';
/** Every node.property the clips animate. */
const channels = (...clips: THREE.AnimationClip[]) => new Set(clips.flatMap((c) => c.tracks.map((t) => t.name)));
/** A one-key track holding the node's property as it is posed now. */
function still(root: THREE.Object3D, channel: string) {
  const [n, prop] = channel.split('.') as [string, Prop];
  const b = root.getObjectByName(n);
  if (!b || !['position', 'quaternion', 'scale'].includes(prop)) return null;
  return prop === 'quaternion'
    ? new THREE.QuaternionKeyframeTrack(channel, [0], b.quaternion.toArray())
    : new THREE.VectorKeyframeTrack(channel, [0], b[prop].toArray());
}

/** Replace the clip's arm tracks with the arms as they are posed now (the hold's grip): rotation, position and scale,
 *  so a grip that a position channel helps make survives (Astra, 2026-10-25). */
function carry(root: THREE.Object3D, clip: THREE.AnimationClip, hold: THREE.AnimationClip) {
  const arm = (ch: string) => ARMS.includes(ch.split('.')[0]);
  const keep = [...channels(clip, hold)].filter(arm);
  clip.tracks = clip.tracks.filter((t) => !arm(t.name));
  for (const ch of keep) { const t = still(root, ch); if (t) clip.tracks.push(t); }
}

/** An attack made from the pose the unit holds now: wind-up, lunge, recover, 0.8 s. Every bone keeps the hold except
 *  the hips (back, then forward and down) and the chest (back, then leaning into the strike). */
function thrust(root: THREE.Object3D, hips: THREE.Object3D, hold: THREE.AnimationClip, h: number) {
  const T = [0, 0.3, 0.45, 0.8];
  const tracks: THREE.KeyframeTrack[] = [];
  const spine = root.getObjectByName('Spine01') ?? root.getObjectByName('Spine');
  // every channel the hold animates stays as posed, except the two this move drives
  const driven = new Set([`${hips.name}.position`, spine && `${spine.name}.quaternion`]);
  for (const ch of channels(hold)) { const t = !driven.has(ch) && still(root, ch); if (t) tracks.push(t); }
  // hips: an offset in the unit's own space (+z forward), taken into the hips' parent space
  const base = root.worldToLocal(hips.getWorldPosition(new THREE.Vector3()));
  const hipAt = (z: number, y: number) => hips.parent!.worldToLocal(root.localToWorld(base.clone().add(new THREE.Vector3(0, y * h, z * h)))).toArray();
  tracks.push(new THREE.VectorKeyframeTrack(`${hips.name}.position`, T, [...hipAt(0, 0), ...hipAt(-0.06, 0), ...hipAt(0.18, -0.05), ...hipAt(0, 0)]));
  if (spine) {
    // a pitch about the unit's side axis (+x leans forward), taken into the chest bone's own frame
    const parentQ = root.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(spine.parent!.getWorldQuaternion(new THREE.Quaternion()));
    const pitch = (deg: number) => {
      const r = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), (deg * Math.PI) / 180);
      return parentQ.clone().invert().multiply(r).multiply(parentQ).multiply(spine.quaternion).toArray();
    };
    tracks.push(new THREE.QuaternionKeyframeTrack(`${spine.name}.quaternion`, T, [...pitch(0), ...pitch(-5), ...pitch(8), ...pitch(0)]));
  }
  const clip = new THREE.AnimationClip('attack', 0.8, tracks);
  STRIKE.set(clip, T[2]);
  return clip;
}

/** A one-key clip `name` holding `clip` at the frame where the feet stand closest together (a stance, not a stride):
 *  the standing pose for a unit whose file has no idle (2026-10-26: the other armies' siege crews are their spearmen,
 *  rigged with the charge, so no Meshy idle). Leaves the rest pose as it found it. */
export function standClip(root: THREE.Object3D, clip: THREE.AnimationClip, name: string) {
  const feet = ['LeftFoot', 'RightFoot'].map((n) => root.getObjectByName(n));
  const rest = new Map<THREE.Object3D, [THREE.Vector3, THREE.Quaternion, THREE.Vector3]>();
  root.traverse((o) => rest.set(o, [o.position.clone(), o.quaternion.clone(), o.scale.clone()]));
  const mixer = new THREE.AnimationMixer(root), act = mixer.clipAction(clip).play();
  let best = 0, gap = Infinity;
  for (let k = 0; k <= 60 && feet[0] && feet[1]; k++) {
    mixer.setTime((clip.duration * k) / 60); root.updateMatrixWorld(true);
    const a = feet[0].getWorldPosition(_a), b = feet[1].getWorldPosition(_b), d = Math.hypot(a.x - b.x, a.z - b.z);
    if (d < gap) { gap = d; best = (clip.duration * k) / 60; }
  }
  mixer.setTime(best); root.updateMatrixWorld(true);
  const tracks = [...channels(clip)].map((ch) => still(root, ch)).filter((t): t is THREE.KeyframeTrack => !!t);
  act.stop(); mixer.uncacheRoot(root);
  for (const [o, [p, q, sc]] of rest) { o.position.copy(p); o.quaternion.copy(q); o.scale.copy(sc); }
  root.updateMatrixWorld(true);
  return new THREE.AnimationClip(name, 1, tracks);
}

/** Swap every prop on this unit (or its clone) to its grip for the clip that is starting. */
export function aimProps(root: THREE.Object3D, clip: string) {
  root.traverse((o) => { const q = o.userData.prop && o.userData.aims?.[clip]; if (q) o.quaternion.fromArray(q); });
}
